import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { translate } from 'react-i18next';
import i18n from 'i18next';

// Components
import Page from '../../templates/component/Page';
import Slot from '../../templates/component/Slot';
import Image from '../../templates/component/Image';
import Text from '../../templates/component/Text';
import Map from '../../templates/component/Map';
import BuildingProperties from '../../templates/component/BuildingProperties';
import Pictogram from '../../templates/component/Pictogram';
import Contacts from '../../templates/component/Contacts';
import Contact from '../../templates/component/Contact';
import Accessibility from '../../templates/component/Accessibility';
import Responsibility from '../../templates/component/Responsibility';

import layoutConfig from '../../layoutConfig';

import { ReadOnlyNotice } from './styles';

const components = {
	image: Image,
	text: Text,
	map: Map,
	buildingProperties: BuildingProperties,
	pictogram: Pictogram,
	contacts: Contacts,
	contact: Contact,
	accessibility: Accessibility,
	responsibility: Responsibility
};

class Template extends Component {
	page = React.createRef();
	state = {
		offsetX: !this.props.editable && !this.props.showReadOnlyNotice ? 0 : 120,
		offsetY: !this.props.editable && !this.props.showReadOnlyNotice ? 0 : 80
	};

	componentDidMount = () => {
	  const scale = this._getScale();
	  const alignment = this._getAlignment();

	  this._scaleAndAlign({
	    scale,
	    ...alignment
	  });

	  this.resizeTimeout = null;

	  window.addEventListener('resize', () => {
	    this.resizeTimeout = window.setTimeout(() => {
	      window.clearTimeout(this.resizeTimeout);
	      this._scaleAndAlign();
	    }, 200);
	  });
	}

	componentDidUpdate = (nextProps) => {
		this._scaleAndAlign();
		if (nextProps.editable !== this.props.editable || nextProps.showReadOnlyNotice !== this.props.showReadOnlyNotice) {
			this.setState({
				offsetX: !this.props.editable && !this.props.showReadOnlyNotice ? 0 : 120,
				offsetY: !this.props.editable && !this.props.showReadOnlyNotice ? 0 : 80
			});
		}
	}

	_scaleAndAlign = () => {
		if (this.page.current === null) {
			return;
		}

		// First we reset the size and alignment
		this.page.current.style.transform = 'scale(1) translate(0px, 0px)';

		// Then we scale
		const scale = this._getScale();
		this.page.current.style.transform = `scale(${scale}) translate(0px, 0px)`;

		// Then we align with the parent
		const alignment = this._getAlignment();
		const transform = `
				translate(${alignment.translateX}px, ${alignment.translateY}px)
				scale(${scale})
			`;

		this.page.current.style.transform = transform;
	}

	_getAlignment = () => {
	  const pageElement = this.page.current;
	  const parentElement = pageElement.parentElement;

	  const pageDimensions = pageElement.getBoundingClientRect();
	  const parentDimensions = parentElement.getBoundingClientRect();

	  const translateX = Math.max(0, Math.round(((parentDimensions.width - this.state.offsetX) - pageDimensions.width) / 2));
	  const translateY = Math.max(0, Math.round(((parentDimensions.height - this.state.offsetY) - pageDimensions.height) / 2));

	  return {
	    translateX,
	    translateY,
	  }
	}

	_getScale = () => {
	  const pageElement = this.page.current;
	  const parentElement = pageElement.parentElement;

	  const { width, height } = pageElement.getBoundingClientRect();

	  const parentRect = parentElement.getBoundingClientRect();
	  const parentDimensions = {
	    width: parentRect.width - this.state.offsetX,
	    height: parentRect.height - this.state.offsetY,
	  };

	  const widthAspect = parentDimensions.width / width;
	  const heightAspect = parentDimensions.height / height;

	  const scale = Math.round(Math.min(heightAspect, widthAspect) * 1000) / 1000;

	  return scale;
	}

	activeSlotHandler = index => {
	  if (index !== this.props.activeSlot) {
	    if (Object.keys(this.props.unsavedChanges).length === 0 || window.confirm(this.props.t('brochure/Confirm slot change with unsaved changes'))) {
	      this.props.resetChangesHandler();
	      this.props.activeSlotHandler(index);
	    }
	  }
	}

	render() {
	  const {
	    data,
	    unsavedChanges,
	    activeSlot,
	    controlsOpen,
	    user,
			config
	  } = this.props;
	  const { language } = i18n;
	  const { layout } = data;
	  const editable = typeof this.props.editable !== 'undefined' ? this.props.editable : true;

	  const slot = data.slot.map((slot, index) => {
	    const { data, options, type } = slot;
	    const Component = components[type];
	    const editorState = type === 'text'
				&& index in this.props.editorState
				&& this.props.editorState[index][language] !== ''
	      ? this.props.editorState[index][language]
	      : false;
	    const componentTypeData = {};

			switch(type) {
			case 'pictogram':
				componentTypeData.buildingFieldLocalNames = config.buildingFieldLocalNames;
				componentTypeData.pictograms = this.props.pictograms;
				break;
			case 'contacts':
			case 'contact':
				componentTypeData.building = this.props.building;
				componentTypeData.users = this.props.users;
				break;
			case 'accessibility':
				componentTypeData.buildingFieldLocalNames = config.buildingFieldLocalNames;
				componentTypeData.building = this.props.building;
				break;
			case 'buildingProperties':
				componentTypeData.buildingFieldLocalNames = config.buildingFieldLocalNames;
				componentTypeData.building = this.props.building;
				break;
			case 'map':
				componentTypeData.building = this.props.building;
				break;
			case 'responsibility':
				componentTypeData.building = this.props.building;
				componentTypeData.certificates = config.buildingCertificates;
				componentTypeData.responsibilities = config.buildingResponsibilities;
				break;
			default:
				break;
			}

	    return (
	      <Slot
	        key={index + '' + type}
	        active={activeSlot === index}
	        highlight={activeSlot === null && controlsOpen}
	        activeSlotHandler={() => this.activeSlotHandler(index)}
	        options={options}
	        type={type}
	        index={index}
	        editable={editable}
	        id={slot.id}
	      >
	        <Component
	          language={language}
	          lowres={this.props.lowres}
	          options={options}
	          componentData={data}
	          unsavedChanges={activeSlot === index ? unsavedChanges : {}}
	          itemActiveIndex={this.props.itemActiveIndex}
	          editorState={editorState}
	          slotIndex={index}
	          changesHandler={this.props.changesHandler}
	          updateEditorState={editorState => this.props.updateEditorState(index, editorState)}
	          {...componentTypeData}
	        />
	      </Slot>
	    );
	  });

	  const className = 'b-layout ' + layoutConfig[layout].className;
	  const showNotice = !this.props.editable && this.props.showReadOnlyNotice;
	  return (
	    <Page
	      options={data.options}
	      innerRef={this.page}
	      className={layout}
	    >
	      <div className={className}>
	        {slot}
	        {showNotice && (<ReadOnlyNotice>{this.props.t('brochure/Read only')}</ReadOnlyNotice>)}
	      </div>
	      {this.props.children}
	    </Page>
	  );
	}
}

Template.propTypes = {
	t: PropTypes.func,
	children: PropTypes.any,
	brochureId: PropTypes.string,
	pageId: PropTypes.string,
	activeSlot: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.number,
		PropTypes.bool,
	]),
	editable: PropTypes.bool,
	showReadOnlyNotice: PropTypes.bool,
	lowres: PropTypes.bool,
	config: PropTypes.object,
	users: PropTypes.array,
	user: PropTypes.object,
	building: PropTypes.object,
	pictograms: PropTypes.array,
	data: PropTypes.object,
	unsavedChanges: PropTypes.object,
	itemActiveIndex: PropTypes.number,
	page: PropTypes.shape({ current: PropTypes.HTMLDivElement }),
	controlsOpen: PropTypes.bool,
	editorState: PropTypes.object,
	activeSlotHandler: PropTypes.func,
	resetChangesHandler: PropTypes.func,
	toggleControlsHandler: PropTypes.func,
	changesHandler: PropTypes.func,
	updateEditorState: PropTypes.func,
}

export default translate('translations')(Template)
